"use client";

import React, { useState } from "react";
import type { Order, OrderStatus } from "./types";
import { ORDER_STATUS, OrderStages, OrderStatusValue } from "@/app/enums/orders";
import { StatusBadge } from "./status-badge";
import { OrderDetails } from "./order-details";
import { UEListData } from "@/app/interface/homepage";
import { ButtonSize, ButtonVariant, RegularButton } from "@/stories/button/button";
import { formatIndianCurrency } from "@/app/utils/helper";
import useMobile from "@/app/hooks/use-mobile";
import { OrderTimeline } from "./order-timeline";
import moment from "moment";
interface OrderTableProps {
    orderData: UEListData[];
    className?: string;
    mobileTableUIOpened: boolean;
    setmobileTableUIOpened: React.Dispatch<React.SetStateAction<boolean>>;
}

type TimelineItem = {
    status?: "success" | "failed" | "inProgress" | "pending";
    timestamp?: string;
    title?: string;
};

type Item = {
    status?: string;
    timeline?: {
        orderCancelled?: TimelineItem;
        orderCreated?: TimelineItem;
        paymentReceived?: TimelineItem;
        orderProcessed?: TimelineItem;
        orderSettled?: TimelineItem;
    };
    estimatedSettlementDate?: string;
};

type Stage = {
    label: string;
    value: string;
    date: string | null;
    status?: string;
    actualSettlementDate?: string | null;
    isCompleted: boolean;
};

export function RecentTransactions({
    orderData,
    className = "",
    mobileTableUIOpened,
    setmobileTableUIOpened,
}: OrderTableProps) {
    const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
    const [mobileOrderExpanded, setmobileOrderExpanded] = useState<Order>();
    const [showAll, setshowAll] = useState<boolean>(false);
    const {isMobile} = useMobile();

    const toggleRow = (orderId: string) => {
        const newExpandedRows = new Set(expandedRows);
        if (expandedRows.has(orderId)) {
            newExpandedRows.delete(orderId);
        } else {
            newExpandedRows.add(orderId);
        }
        setExpandedRows(newExpandedRows);
    };

    const getOrderStages = (item: Item): { stages: Stage[]; lastCompletedStage: string } => {
        const stages: Stage[] = [];

        // Stage 1: Order Created
        stages.push({
            label: item?.status ?? "Order Created",
            value: item.timeline?.orderCreated?.status !== "success" ? "Order Failed" : "Order Created",
            date: item.timeline?.orderCreated?.timestamp
                ? new Date(item.timeline.orderCreated.timestamp)
                    .toLocaleString("en-IN", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        hour: "numeric",
                        minute: "numeric",
                        hour12: true,
                    })
                    .replace("am", "AM")
                    .replace("pm", "PM")
                : null,
            isCompleted: item.timeline?.orderCreated?.status === "success",
        });

        // Stage 2: Order Cancelled (only add if applicable)
        if (item.timeline?.orderCancelled) {
            stages.push({
                label: "Order Cancelled",
                value: "Order Cancelled",
                date: item.timeline.orderCancelled.timestamp
                    ? new Date(item.timeline.orderCancelled.timestamp)
                        .toLocaleString("en-IN", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                            hour: "numeric",
                            minute: "numeric",
                            hour12: true,
                        })
                        .replace("am", "AM")
                        .replace("pm", "PM")
                    : null,
                isCompleted: item.timeline.orderCancelled.status === "success",
            });
        }

        // Stage 3: Payment Received
        if (item.timeline?.paymentReceived) {
            stages.push({
                label: item?.status ?? "Payment Received",
                value: item?.status === OrderStages.OrderFailed ? item.timeline.paymentReceived.title ?? "" : "Payment Received",
                date: item.timeline.paymentReceived.timestamp
                    ? new Date(item.timeline.paymentReceived.timestamp)
                        .toLocaleString("en-IN", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                            hour: "numeric",
                            minute: "numeric",
                            hour12: true,
                        })
                        .replace("am", "AM")
                        .replace("pm", "PM")
                    : null,
                isCompleted: 
                    item.timeline.paymentReceived.status === "success" ||
                    item.timeline.paymentReceived.status === "inProgress",
            });
        }

        // Stage 4: Order Processed
        if (item.timeline?.orderProcessed) {
            stages.push({
                label:
                    item.timeline.orderProcessed.status !== "success" &&
                    item.timeline.orderProcessed.status !== "inProgress" &&
                    item.timeline.orderProcessed.status !== "pending"
                        ? "Order Rejected"
                        : "Order Processed",
                value:
                    item.timeline.orderProcessed.status !== "success" &&
                    item.timeline.orderProcessed.status !== "inProgress" &&
                    item.timeline.orderProcessed.status !== "pending"
                        ? "Order Rejected"
                        : "Order Processed",
                date: item.timeline.orderProcessed.timestamp
                    ? new Date(item.timeline.orderProcessed.timestamp)
                        .toLocaleString("en-IN", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                            hour: "numeric",
                            minute: "numeric",
                            hour12: true,
                        })
                        .replace("am", "AM")
                        .replace("pm", "PM")
                    : null,
                status: item.timeline.orderProcessed.status,
                isCompleted: item.timeline.orderProcessed.status === "success",
            });
        }

        // Stage 5: Order Settled (only add if there's settlement data)
        if (item?.estimatedSettlementDate || item.timeline?.orderSettled) {
            stages.push({
                label: "Order Settled",
                value: "Order Settled",
                date: item?.estimatedSettlementDate
                    ? new Date(item.estimatedSettlementDate)
                        .toLocaleString("en-IN", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                            hour: "numeric",
                            minute: "numeric",
                            hour12: true,
                        })
                        .replace("am", "AM")
                        .replace("pm", "PM")
                    : null,
                actualSettlementDate: item.timeline?.orderSettled?.timestamp 
                    ? `Actual: ${new Date(item.timeline.orderSettled.timestamp)
                        .toLocaleString("en-IN", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                            hour: "numeric",
                            minute: "numeric",
                            hour12: true,
                        })
                        .replace("am", "AM")
                        .replace("pm", "PM")}`
                    : null,
                isCompleted: item.timeline?.orderSettled?.status === "success",
            });
        }

        if (item.timeline?.orderCreated?.status === "failed" && stages[0]?.label) {
            stages[0].label = OrderStatusValue.Failed;
            stages[0].isCompleted = true;
            return {
                stages,
                lastCompletedStage: OrderStatusValue.Failed,
            };
        }

        if (stages[1]?.label && stages[1].label === "Payment Received" && stages[2].status === "inProgress") {
            stages[2].isCompleted = true;
            return {
                stages,
                lastCompletedStage: OrderStatusValue.Processing,
            };
        }

        // Find the last completed stage
        const lastCompletedStage =
            [...stages].reverse().find((stage) => stage.isCompleted)?.label ||
            "No stages completed";

        return { stages, lastCompletedStage };
    };

    const transformOrders = (apiData: UEListData[]): Order[] => {
        return (
            apiData &&
            apiData.map((item) => {
                const stages = getOrderStages(item);
                const lastCompletedStage: OrderStages =
                    (stages.lastCompletedStage as OrderStages) || OrderStages.OrderCreated;
                return {
                    id: item.orderId || "",
                    productName: item.displayName || "",
                    status: lastCompletedStage as OrderStages,
                    date: item.date || "N/A",
                    amount: item?.amountBreakup?.userAmount,
                    basketTxns: item.basketTxns ?? [],
                    isTimeline: item?.timeline ? true : false,
                    oldStatus: (item?.status as OrderStages) || OrderStages.OrderCreated,
                    details: {
                        type: item.txnType === "purchase" ? "Purchase" : item.txnType || "",
                        pricePerShare: item.unitPrice,
                        units: item.units,
                        transactionId: item.orderId || "",
                        stages: stages.stages,
                    },
                };
            })
        );
    };

    const orders: Order[] = transformOrders(orderData);

    return (
        <div className="mt-6 w-full max-w-[812px]">
            <div className="flex flex-col justify-between gap-3 pb-6 sm:flex-row sm:items-center">
                <h3 className="text-xl font-semibold sm:text-3xl">Recent Transactions</h3>
            </div>
            {!isMobile ? (
                <div className={`mx-auto w-full max-w-5xl ${className}`}>
                    <div>
                        <table className="w-full">
                            <thead>
                                <tr className="border-b-2 border-black">
                                    <th className="px-6 py-3 text-left text-md font-semibold leading-[160%]">
                                        Product Name
                                    </th>
                                    <th className="px-6 py-3 text-left text-md font-semibold leading-[160%]">
                                        Status
                                    </th>
                                    <th className="px-6 py-3 text-left text-md font-semibold leading-[160%]">
                                        Date
                                    </th>
                                    <th className="px-6 py-3 text-left text-md font-semibold leading-[160%]">
                                        Amount
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {orders
                                    ?.slice(0, showAll ? orders.length : 6)
                                    .map((order, index) => (
                                        <React.Fragment key={index}>
                                            <tr
                                                key={order.id}
                                                className={
                                                    expandedRows.has(order.id)
                                                        ? "bg-neutral-50 transition-all duration-300 ease-in-out"
                                                        : "transition-all duration-300 ease-in-out"
                                                }
                                            >
                                                <td className="px-6 py-4 text-sm font-medium leading-[160%] tracking-[0.14px]">
                                                    {order.productName}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <button
                                                        onClick={() => {
                                                            if (order.details && order.isTimeline) {
                                                                toggleRow(order.id);
                                                            }
                                                        }}
                                                        className="cursor-pointer"
                                                        disabled={!order.details}
                                                    >
                                                        {/* TODO: TS Bug fix for order.status */}
                                                        <StatusBadge
                                                            status={
                                                                ORDER_STATUS[
                                                                    order.isTimeline
                                                                        ? order.status
                                                                        : order.oldStatus
                                                                ] as unknown as OrderStatus
                                                            }
                                                            isExpanded={expandedRows.has(order.id)}
                                                            showChevron={!!order.details && !!order.isTimeline}
                                                        />
                                                    </button>
                                                </td>
                                                <td className="px-6 py-4 font-medium leading-[160%] tracking-[0.14px]">
                                                    {moment(order?.date, "MM/DD/YYYY").format("DD MMM YYYY")}
                                                </td>
                                                <td className="px-6 py-4 font-medium leading-[160%] tracking-[0.14px]">
                                                    {formatIndianCurrency(order.amount)}
                                                </td>
                                            </tr>

                                            {order.details && expandedRows.has(order.id) && (
                                                <>
                                                    <tr className="border-b transition-all duration-300 ease-in-out">
                                                        <td colSpan={4} className="p-4">
                                                            <OrderDetails
                                                                details={order.details}
                                                                basketTxns={order.basketTxns}
                                                            />
                                                        </td>
                                                    </tr>
                                                </>
                                            )}
                                        </React.Fragment>
                                    ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : (
                orders
                    ?.slice(0, orders.length > 1 && !showAll ? 1 : orders.length)
                    .map((order, index) => (
                        <div
                            className={`overflow-auto rounded-lg border-[1px] border-neutralOpacityDark-10 ${orders.length > 1 ? "mb-5" : ""}`}
                            key={index}
                        >
                            <div className="flex justify-between border-b-[1px] border-neutralOpacityDark-10">
                                <p className="px-s7 py-4 text-xs font-medium leading-[160%] tracking-[0.12px]">
                                    Product Name
                                </p>
                                <p className="px-s7 py-4 text-right text-xs font-medium leading-[160%] tracking-[0.12px]">
                                    {order.productName}
                                </p>
                            </div>
                            <div className="flex justify-between border-b-[1px] border-neutralOpacityDark-10">
                                <p className="px-s7 py-4 text-xs font-medium leading-[160%] tracking-[0.12px]">
                                    Status
                                </p>
                                <p className="px-s7 py-4 text-xs font-medium leading-[160%] tracking-[0.12px]">
                                    <button
                                        onClick={() => {
                                            if (order.details && !mobileOrderExpanded && order.isTimeline) {
                                                setmobileTableUIOpened(true);
                                                setmobileOrderExpanded(order);
                                            } else {
                                                setmobileTableUIOpened(false);
                                                setmobileOrderExpanded(undefined);
                                            }
                                        }}
                                        className="cursor-pointer"
                                        disabled={!order.details}
                                    >
                                        {/* TODO: TS Bug fix for order.status */}
                                        <StatusBadge
                                            status={
                                                ORDER_STATUS[
                                                    order.isTimeline
                                                        ? order.status
                                                        : order.oldStatus
                                                ] as unknown as OrderStatus
                                            }
                                            isExpanded={expandedRows.has(order.id)}
                                            showChevron={!!order.details && !!order.isTimeline}
                                        />
                                    </button>
                                </p>
                            </div>
                            <div className="flex justify-between border-b-[1px] border-neutralOpacityDark-10">
                                <p className="px-s7 py-4 text-xs font-medium leading-[160%] tracking-[0.12px]">
                                    Date
                                </p>
                                <p className="px-s7 py-4 text-xs font-medium leading-[160%] tracking-[0.12px] text-neutral-300">
                                    {moment(order?.date, "MM/DD/YYYY").format("DD MMM YYYY")}
                                </p>
                            </div>
                            <div className="flex justify-between">
                                <p className="px-s7 py-4 text-xs font-medium leading-[160%] tracking-[0.12px]">
                                    Amount
                                </p>
                                <p className="px-s7 py-4 text-xs font-medium leading-[160%] tracking-[0.12px]">
                                    {formatIndianCurrency(order.amount)}
                                </p>
                            </div>
                            {order?.basketTxns &&
                                order?.basketTxns.map((txn, index) => {
                                    return (
                                        <div
                                            key={index}
                                            className="flex h-[44px] w-full items-center justify-start gap-3 px-[9.58px] py-[6.38px]"
                                        >
                                            <p
                                                title={txn?.displayName}
                                                className="line-clamp-2 min-w-[22%] max-w-[30%] truncate text-wrap text-left text-2xs font-semibold leading-[160%]"
                                            >
                                                {txn?.displayName}
                                            </p>
                                            <p className="text-nowrap text-center text-2xs">
                                                Purchase
                                            </p>
                                            <StatusBadge
                                                status={
                                                    ORDER_STATUS[
                                                        txn?.status
                                                    ] as unknown as OrderStatus
                                                }
                                                isExpanded={false}
                                                showChevron={false}
                                            />
                                            <p className="text-2xs">
                                                {formatIndianCurrency(txn?.amount ?? 0)}
                                            </p>
                                            <p className="text-2xs">{txn?.units}</p>
                                        </div>
                                    );
                                })}
                        </div>
                    ))
            )}
            {mobileOrderExpanded && isMobile && mobileTableUIOpened ? (
                <OrderTimeline
                    orderDetails={mobileOrderExpanded}
                    mobileOrderExpanded={Boolean(mobileOrderExpanded)}
                    setmobileOrderExpanded={setmobileTableUIOpened}
                />
            ) : (
                <></>
            )}
            {orders?.length > (isMobile ? 1 : 6) && (
                <div className="flex items-center justify-center">
                    <RegularButton
                        variant={ButtonVariant.SecondaryDefault}
                        noIcon
                        className="mt-6"
                        showRightIcon={false}
                        size={isMobile ? ButtonSize.Medium : ButtonSize.Large}
                        onClick={() => setshowAll(!showAll)}
                        label={`Show ${showAll ? "Less" : "More"}`}
                    />
                </div>
            )}
        </div>
    );
}
